/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { Observable } from "rxjs";
import { ajax, AjaxRequest, AjaxResponse } from "rxjs/ajax";
import { map } from "rxjs/operators";

import Api, { ApiStatus, HttpMethod } from "./Api";
import ApiConfig from "./ApiConfig";

import { AuthOpenIdConnect, AuthOpenIdConnectConfig } from "./auth/AuthOpenIdConnect";
import { AuthProviders, AuthProviderType } from "./auth/AuthProviders";

import Auth from "./auth/Auth";
import CompanyDataProvider from "./company/CompanyDataProvider";
import CpeDataProvider from "./cpe/CpeDataProvider";
import { InfoDTO } from "data/dto/info/InfoDTO";


export default class ApiDefault implements Api {

   public get config(): ApiConfig { return this.apiConfig; }

   private _status: ApiStatus;
   public get status(): ApiStatus { return this._status; }

   private _version: string;
   public get version(): string { return this._version; }

   private _copyright: string;
   public get copyright(): string { return this._copyright; }

   private _csrf_token: string;
   public get csrf_token(): string { return this._csrf_token; }

   private _user_id: string | null;
   public get user_id(): string | null { return this._user_id; }

   private _user_roles: string[];
   public get user_roles(): string[] { return this._user_roles; }

   private _auth_expires: number;
   public get auth_expires(): number { return this._auth_expires; }

   private _auth_providers: AuthProviders;
   public get auth_providers(): AuthProviders { return this._auth_providers; }

   private _auth: Auth | null;
   public get auth(): Auth | null { return this._auth; }

   private _companyDataProvider: CompanyDataProvider;
   public get companyDataProvider(): CompanyDataProvider { return this._companyDataProvider; }

   private _cpeDataProvider: CpeDataProvider;
   public get cpeDataProvider(): CpeDataProvider { return this._cpeDataProvider; }


   constructor(
      private apiConfig: ApiConfig,
   ) {

      this._status = ApiStatus.NotInitialized;
      this._version = "";
      this._copyright = "";
      this._csrf_token = "";
      this._user_id = null;
      this._user_roles = [ "anonymous" ];
      this._auth_expires = 0;
      this._auth_providers = {};

      this._auth = null;
      this._companyDataProvider = new (apiConfig.companyDataProviderCtor)(this);
      this._cpeDataProvider = new (apiConfig.cpeDataProviderCtor)(this);

   }

   /**
    * Initializes API.
    * Calls /api/info and collects all information.
    * Should be called and awaited when the application is initializing.
    */
   public async initAsync(): Promise<void> {

      this._status = ApiStatus.Initializing;

      return new Promise((resolve, reject) => {

         const observable$ = ajax({
            url: `${this.apiConfig.apiBaseUrl}/info`,
            withCredentials: true
         });

         observable$.subscribe({

            next: (response: AjaxResponse<any>) => {

               const result = this.processInitResponse(response.response as InfoDTO);

               if (result) {
                  this._status = ApiStatus.Ready;
                  resolve();
               } else {
                  reject();
               }

            },

            error: (error: any) => {
               this._status = ApiStatus.Error;
               reject(error);
            }

         });

      });


   }


   /**
    * Performs an API call. Setups the AJAX call according to
    * requirements andconfiguration i.e. adds CSRF token and so on.
    * It is expected that all API calls returns 200 if the request
    * is served successfully. Otherwise an error is thrown
    * @param endpoint API endpoint to call
    * @param search Object containing search arguments (behind path in url, after ? mark)
    * @param method Method to be used (valid methods are GET, POST, PUT, DELETE)
    * @param data Data to be passed in the body (i.e. JSON object)
    */
   public call<RequestBody = any, ResponseBody = any>(endpoint: string, search: any = {}, method: HttpMethod = "GET", data?: RequestBody): Observable<ResponseBody> {

      if (this._status !== ApiStatus.Ready) throw new Error("Api not ready!");

      const headers: any = {
         "X-CSRF-Token": this.csrf_token
      };

      if (typeof data === "object") {
         headers["Content-Type"] = "application/json";
      }

      const url = new URL(`${this.config.apiBaseUrl}${endpoint}`);

      Object.keys(search).forEach(
         key => { if (search[key]) url.searchParams.append(key, search[key].toString()) }
      );

      const ajaxRequest: AjaxRequest = {
         url:  url.toString(),
         withCredentials: true,
         method: method,
         async: true,
         crossDomain: false,
         timeout: 30000,
         responseType: "json",
         headers
      }

      ajaxRequest.crossDomain = window.location.hostname !== url.hostname;
      if (data) ajaxRequest.body = data;

      return ajax<any>(ajaxRequest).pipe(
         map((response) => {
            if (response.responseHeaders["x-tk-auth-expires"]) {
               this._auth_expires = parseInt(response.responseHeaders["x-tk-auth-expires"]) + Date.now();
            }
            if (response.status === 200) return response.response;
            throw new Error(`Unexpected server response status: ${response.status}`);
         }
      ));

   }


   /**
    * Processes a successfull info response from the server
    * @param info Info object returned from the API
    */
   private processInitResponse(info: InfoDTO): boolean {

      if (info.version) this._version = info.version;
      if (info.copyright) this._copyright = info.copyright;
      if (info.csrf_token) this._csrf_token = info.csrf_token;
      if (info.user_id) this._user_id = info.user_id;
      if (info.user_roles) this._user_roles = info.user_roles;
      if (info.auth_expires) this._auth_expires = info.auth_expires + Date.now();
      if (info.auth_providers) this._auth_providers = info.auth_providers;

      if (this._auth_providers) this.setDefaultAuthProvider();

      // These values are mandatory and must be always be present in the info header
      if (info.version && info.copyright && info.csrf_token) return true;

      return false;

   }


   /**
    * Based on obtained info the first obtained configuration of
    * auth provider is used to instantiate the default auth provider.
    * It is just preparation if multiple providers will be used in future
    */
   private setDefaultAuthProvider(): void {

      const providers = Object.keys(this._auth_providers);
      if (providers.length === 0) return;

      const defaultproviderName = providers[0];
      const defaultProviderConfig = this._auth_providers[defaultproviderName];

      switch (defaultProviderConfig.type) {

         case AuthProviderType.OpenIdConnect:

            this._auth = new AuthOpenIdConnect({
               friendlyName: defaultProviderConfig.friendlyName,
               type: AuthProviderType.OpenIdConnect,
               login: `/auth${(defaultProviderConfig as AuthOpenIdConnectConfig).login}`,
               logout: `/auth${(defaultProviderConfig as AuthOpenIdConnectConfig).logout}`,
            }, this);

            break;

      }
   }


}